import { differenceInDays, differenceInMilliseconds } from 'date-fns';

/**
 * Utility functions for managing UPFT (Upgrade Free Pool Target) modal display logic
 */

export interface StorageAdapter {
	get: (key: string) => string | null;
	removeItem: (key: string) => void;
	set: (key: string, value: string) => void;
}

/**
 * Supported product types for the upgrade modal flow
 */
export enum Product {
	Confluence = 'Confluence',
	Jira = 'Jira',
}

/**
 * Enum representing the specific modal components that can be shown
 */
export enum UpgradeModalComponent {
	// Pre-upgrade modals
	ConfluenceStandardOfferAIModal = 'ConfluenceStandardOfferAIModal',
	ConfluenceStandardOfferCalendarModal = 'ConfluenceStandardOfferCalendarModal',
	JiraPremiumOfferPlansModal = 'JiraPremiumOfferPlansModal',
	JiraPremiumOfferAIModal = 'JiraPremiumOfferAIModal',
	// Post-upgrade modals
	ConfluenceAfterStandardUpgradeAIModal = 'ConfluenceAfterStandardUpgradeAIModal',
	ConfluenceAfterStandardUpgradeCalendarModal = 'ConfluenceAfterStandardUpgradeCalendarModal',
	JiraAfterPremiumUpgradePlansModal = 'JiraAfterPremiumUpgradePlansModal',
	JiraAfterPremiumUpgradeAIModal = 'JiraAfterPremiumUpgradeAIModal',
	// Common
	None = 'None',
}

const DAYS_BETWEEN_MODALS = 10;
const REACT_RERENDER_GRACE_PERIOD_MS = 1500;

export const STORAGE_KEYS = {
	[Product.Confluence]: {
		first: 'atl-UPFTConfluenceStandardOfferAIModalTimestamp',
		second: 'atl-UPFTConfluenceStandardOfferCalendarModalTimestamp',
	},
	[Product.Jira]: {
		first: 'atl-UPFTJiraPremiumOfferPlansModalTimestamp',
		second: 'atl-UPFTJiraPremiumOfferAIModalTimestamp',
	},
} as const;

const MODAL_COMPONENTS = {
	[Product.Confluence]: {
		first: UpgradeModalComponent.ConfluenceStandardOfferAIModal,
		second: UpgradeModalComponent.ConfluenceStandardOfferCalendarModal,
	},
	[Product.Jira]: {
		first: UpgradeModalComponent.JiraPremiumOfferPlansModal,
		second: UpgradeModalComponent.JiraPremiumOfferAIModal,
	},
} as const;

/**
 * Records that the first modal has been shown for the specified product
 * @param storageAdapter - Generic storage adapter with get/set methods
 * @param product - The product type (Confluence or Jira)
 */
export function recordFirstModalShown(storageAdapter: StorageAdapter, product: Product): void {
	const now = Date.now();
	const key = STORAGE_KEYS[product].first;
	storageAdapter.set(key, now.toString());
}

/**
 * Records that the second modal has been shown for the specified product
 * @param storageAdapter - Generic storage adapter with get/set methods
 * @param product - The product type (Confluence or Jira)
 */
export function recordSecondModalShown(storageAdapter: StorageAdapter, product: Product): void {
	const now = Date.now();
	const key = STORAGE_KEYS[product].second;
	storageAdapter.set(key, now.toString());
}



/**
 * Determines which post-upgrade modal should be shown
 * based on their previous modal viewing history
 * @param storageAdapter - Generic storage adapter with get/set methods
 * @param product - The product type (Confluence or Jira)
 * @returns The specific post-upgrade modal component that should be displayed, or None if not applicable
 */
export function getPostUpgradeModalToShowForUpgradeFreePoolTargetExperiment(
	storageAdapter: StorageAdapter,
	product: Product,
): UpgradeModalComponent {
	const firstModalTimestamp = storageAdapter.get(STORAGE_KEYS[product].first);
	const secondModalTimestamp = storageAdapter.get(STORAGE_KEYS[product].second);

	if (product === Product.Confluence) {
		// If user saw both modals, show Calendar post-upgrade modal
		if (firstModalTimestamp && secondModalTimestamp) {
			return UpgradeModalComponent.ConfluenceAfterStandardUpgradeCalendarModal;
		}
		// If user only saw first modal (AI), show AI post-upgrade modal
		if (firstModalTimestamp) {
			return UpgradeModalComponent.ConfluenceAfterStandardUpgradeAIModal;
		}
	} else if (product === Product.Jira) {
		// If user saw second modal (AI), show AI post-upgrade modal
		if (secondModalTimestamp) {
			return UpgradeModalComponent.JiraAfterPremiumUpgradeAIModal;
		}
		// If user only saw first modal (Plans), show Plans post-upgrade modal
		if (firstModalTimestamp) {
			return UpgradeModalComponent.JiraAfterPremiumUpgradePlansModal;
		}
	}

	// If user hasn't seen any modals, don't show post-upgrade modal
	return UpgradeModalComponent.None;
}

// Helper function to check if we're within React re-render grace period
export const isWithinGracePeriod = (timestamp: string): boolean => {
	const time = parseInt(timestamp, 10);
	return differenceInMilliseconds(Date.now(), new Date(time)) < REACT_RERENDER_GRACE_PERIOD_MS;
};

/**
 * Determines which specific modal component should be shown to the user
 * @param storageAdapter - Generic storage adapter with get/set methods
 * @param product - The product type (Confluence or Jira)
 * @returns The specific modal component that should be displayed
 */
export function getModalToShow(
	storageAdapter: StorageAdapter,
	product: Product,
): UpgradeModalComponent {
	const firstModalTimestamp = storageAdapter.get(STORAGE_KEYS[product].first);
	const secondModalTimestamp = storageAdapter.get(STORAGE_KEYS[product].second);

	// If second modal has been shown and enough time has passed to avoid React re-render issues
	if (secondModalTimestamp) {
		// Show second modal if within grace period, otherwise flow is complete
		return isWithinGracePeriod(secondModalTimestamp)
			? MODAL_COMPONENTS[product].second
			: UpgradeModalComponent.None;
	}

	// If first modal has never been shown, show the first modal
	if (!firstModalTimestamp) {
		return MODAL_COMPONENTS[product].first;
	}

	// If first modal is within grace period of React re-rendering, keep showing it
	if (isWithinGracePeriod(firstModalTimestamp)) {
		return MODAL_COMPONENTS[product].first;
	}

	// Check if enough days have passed for second modal
	const firstModalTime = parseInt(firstModalTimestamp, 10);
	const daysSinceFirstModal = differenceInDays(Date.now(), new Date(firstModalTime));

	return daysSinceFirstModal >= DAYS_BETWEEN_MODALS
		? MODAL_COMPONENTS[product].second
		: UpgradeModalComponent.None;
}
